// In-memory ownership for a live agent SSE request.
//
// Workspace routes are unmounted when an operator backgrounds a chat tab, while
// the tab itself remains open. This registry deliberately outlives that page
// component so backgrounding does not cancel useful work. It is not persistence:
// a refresh has the existing URL/thread restoration contract and the backend is
// still authoritative for session state.

import { api } from "./api";

type ActiveAgentStream = {
  controller: AbortController;
  stopRequested: boolean;
};

type ScopedActiveAgentStream = ActiveAgentStream & { threadId: string };

// A workspace chat tab is `chat:<brandId>`, while the server's stop endpoint
// needs its current thread id. Scope ownership by the tab's brand and retain the
// thread id as request data so the close control cannot confuse the two.
const activeStreams = new Map<string, ScopedActiveAgentStream>();
const listeners = new Set<() => void>();

function emitChange(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeToAgentStreamLifecycle(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The active thread for a chat tab's brand scope, or null when none is live. */
export function getActiveAgentStreamThreadId(brandId: string | null): string | null {
  return brandId ? activeStreams.get(brandId)?.threadId ?? null : null;
}

export function registerAgentStream(brandId: string, threadId: string, controller: AbortController): void {
  activeStreams.set(brandId, { threadId, controller, stopRequested: false });
  emitChange();
}

export function releaseAgentStream(brandId: string | null, controller: AbortController): void {
  if (brandId && activeStreams.get(brandId)?.controller === controller) {
    activeStreams.delete(brandId);
    emitChange();
  }
}

export function hasActiveAgentStream(brandId: string | null): boolean {
  return !!brandId && activeStreams.has(brandId);
}

/**
 * Stop one open chat tab's stream. Clearing the entry first makes this
 * idempotent across an explicit Stop click, tab close, and late page cleanup.
 */
export async function stopAgentStream(brandId: string | null): Promise<boolean> {
  if (!brandId) return false;
  const stream = activeStreams.get(brandId);
  if (!stream || stream.stopRequested) return false;

  stream.stopRequested = true;
  activeStreams.delete(brandId);
  emitChange();
  stream.controller.abort();
  await api.post(`/api/agent/threads/${stream.threadId}/stop`);
  return true;
}
